import React, { useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { setError } from '../../../actions';
import { SectionPlaceholder } from '../../Placeholders/SectionPlaceholder';
import { ItemCardPlaceholder } from '../../Placeholders/ItemCardPlaceholder';
import { ItemCard } from '../../ui/ItemCard';
import { getAllBookmarks } from '../../../api/queries';
import { BookmarksAllQuery } from '../../../graphql/graphql';
import { BookmarkList, EmptyBookmarks, NoResultText } from './styled';
import { PageWrapper } from '../../../styled/pageLayout.styles';
import { AdaptivePageHeader } from '../../ui/PageHeader/AdaptivePageHeader';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const bookmarksVariable = {
  limit: 100
};

export const BookmarksPage: React.FC<{
  setErrorUI: (arg: boolean) => void;
}> = ({ setErrorUI }) => {
  const { t } = useTranslation();

  const { loading, data, error } = useQuery<BookmarksAllQuery>(
    getAllBookmarks,
    {
      variables: bookmarksVariable,
      ssr: false,
      fetchPolicy: 'network-only'
    }
  );

  const emptyLayout = (
    <EmptyBookmarks data-cy="no-bookmarks">
      <Image
        src={'/static/images/favorites.svg'}
        width={150}
        height={276}
        alt="Image"
      />
      <NoResultText>{t('pages.bookmarks.no_results')}</NoResultText>
    </EmptyBookmarks>
  );

  const itemsList = useMemo(() => {
    const bookmarks = data?.bookmarks || [];
    return bookmarks.length
      ? bookmarks.map(bookmark => (
          <ItemCard
            key={bookmark.item.id}
            {...bookmark.item}
            inBookmarks={true}
            shouldUpdateCache={true}
          />
        ))
      : [];
  }, [data?.bookmarks]);

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error]);

  return (
    <PageWrapper>
      <AdaptivePageHeader
        title={t('pages.bookmarks.title')}
        type="bookmarks"
        hasFilters={false}
      />
      {loading ? (
        <SectionPlaceholder noHeader rowCount={2}>
          {Array.from({ length: 5 }, (_, index) => (
            <ItemCardPlaceholder key={index} isCardsLayout />
          ))}
        </SectionPlaceholder>
      ) : (
        <BookmarkList $isEmptyView={!itemsList.length}>
          {itemsList.length ? itemsList : emptyLayout}
        </BookmarkList>
      )}
    </PageWrapper>
  );
};

export default connect(null, mapDispatchToProps)(BookmarksPage);
